import { defineItemStore } from "../itemStore.store";
import { cloneDeep, groupBy } from "lodash-es";
import { mapValues, pickBy } from "@/helpers/utility/CollectionFunctions";
import { evaluateRelation, getFormulaPoints } from "../relation/Relation";
import { useFlowStore } from "./flows.store";
import { useModelStore } from "../model/model.store";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import GraphModel from "../relation/GraphModel";

import type { ExpressionValue } from "../expression/expressionFunctions";
import type { GraphPoint } from "../relation/GraphPoint";
import type { CurveFormType } from "../relation/RelationItem";
import type { FlowBranchItem } from "./FlowBranchItem";
import type { SetRequired } from "type-fest";
import type { FlowItem } from "./FlowItem";
import type { TargetItem } from "../target/TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { IndexItem } from "../index/IndexItem";

export const flowBranchItemStructure: FlowBranchItem = {
  type: "flowBranch",
  uid: "flowBranch_0",
  flowId: 0,

  branchType: "sink",

  elementType: "target",
  elementId: 0,

  curveType: "formula",
  curvePoints: [],
  formula: "x",

  limitsFlow: true,

  createdAt: 0,
  id: -1,
  docs: {}
};

export const useFlowBranchStore = defineItemStore(
  "model/flowBranches",
  flowBranchItemStructure,
  {
    getters: {
      byFlow(state): { [flowId: number]: FlowBranchItem[] } {
        return groupBy(state.items, "flowId");
      },
      byResource(state): { [resourceId: number]: FlowBranchItem[] } {
        return groupBy(
          pickBy(state.items, (branch) => branch.elementType == "resource"),
          "elementId"
        );
      },

      byElementUid(state): { [uid: string]: FlowBranchItem[] } {
        return groupBy(
          state.items,
          (branch) => `${branch.elementType}_${branch.elementId}`
        );
      },
      outgoingByElementUid(state): { [uid: string]: FlowBranchItem[] } {
        return groupBy(
          pickBy(state.items, (branch) => branch.branchType == "source"),
          (branch) => `${branch.elementType}_${branch.elementId}`
        );
      },
      incomingByElementUid(state): { [uid: string]: FlowBranchItem[] } {
        return groupBy(
          pickBy(state.items, (branch) => branch.branchType == "sink"),
          (branch) => `${branch.elementType}_${branch.elementId}`
        );
      },

      graphModels(state): { [id: number]: GraphModel } {
        const graphModels: { [id: number]: GraphModel } = {};
        for (const branchId in state.items) {
          const branch = state.items[branchId];
          graphModels[branch.id] = new GraphModel(branch.curvePoints);
        }
        return graphModels;
      },

      values(state): { [id: number]: number } {
        return mapValues(state.items, (branch) => {
          const flowValue = useFlowStore().values[branch.flowId];
          return evaluateRelation(branch, flowValue);
        });
      },

      expressionValues(state): { [uid: string]: ExpressionValue } {
        const expressionValues: { [uid: string]: ExpressionValue } = {};

        for (const id in state.items) {
          const item = state.items[id];
          const flowValue = useFlowStore().expressionValues[`flow_${item.flowId}`];
          if (flowValue == null) continue;

          // TODO: expression in evaluation
          const value = evaluateRelation(item, Number(flowValue.value));
          const minValue = flowValue.minValue == null ? null : evaluateRelation(item, flowValue.minValue);
          const maxValue = flowValue.maxValue == null ? null : evaluateRelation(item, flowValue.maxValue);

          expressionValues[item.uid] = {
            value,
            minValue: minValue,
            maxValue: maxValue,
            unit: this.elements[item.id]?.unit || "",
            unitValid: true,
            valid: true
          };
        }

        return expressionValues;
      },

      elements(): { [id: number]: TargetItem | IndexItem | ResourceItem } {
        const elements: {
          [id: number]: TargetItem | IndexItem | ResourceItem;
        } = {};

        const targetStore = useTargetStore();
        const resourceStore = useResourceStore();

        for (const branch of this.orderedItems) {
          if (branch.elementType == "target") {
            elements[branch.id] = targetStore.items[branch.elementId];
          } else if (branch.elementType == "resource") {
            elements[branch.id] = resourceStore.items[branch.elementId];
          }
        }

        return elements;
      }
    },
    actions: {
      async createFlow(
        flowData:
          | SetRequired<
              Partial<FlowItem>,
              "valueReferenceType" | "valueReferenceId"
            >
          | SetRequired<Partial<FlowItem>, "valueExpression">,
        ...branchesData: SetRequired<
          Partial<FlowBranchItem>,
          "elementType" | "elementId"
        >[]
      ): Promise<{ flow: FlowItem; branches: FlowBranchItem[] }> {
        const flowStore = useFlowStore();
        const flow = await flowStore.create(flowData);

        const branches = [];
        for (const branchData of branchesData) {
          const branch = await this.create({
            ...branchData,
            flowId: flow.id
          });
          branches.push(branch);
        }
        return { flow, branches };
      },

      /**
       * Create a flow between two elements.
       * @param sourceItem Source element
       * @param sinkItem Sink element
       * @param flowData Any extra data for the flow
       */
      async createFlowBetween(
        sourceItem?:
          | { type: "target"; id: number; }
          | { type: "resource"; id: number; }
          | null,
        sinkItem?:
          | { type: "target"; id: number; }
          | { type: "resource"; id: number; }
          | null,
        flowData?: Partial<FlowItem>
      ): Promise<{ flow: FlowItem; branches: FlowBranchItem[] }> {
        const flowStore = useFlowStore();
        const flow = await flowStore.create({
          valueInputMode: "expression",
          valueExpression: "0",
          ...flowData
        });

        const branches = [];
        if (sourceItem) {
          const sourceBranch = await this.create({
            elementType: sourceItem.type,
            elementId: sourceItem.id,
            flowId: flow.id,
            branchType: "source",
            curveType: "formula",
            formula: "-x"
          });
          branches.push(sourceBranch);
        }

        if (sinkItem) {
          const sinkBranch = await this.create({
            elementType: sinkItem.type,
            elementId: sinkItem.id,
            flowId: flow.id,
            branchType: "sink",
            curveType: "formula",
            formula: "x"
          });
          branches.push(sinkBranch);
        }

        return { flow, branches };
      },

      invertCurve(branch: { id: number }): void {
        const id = branch.id;
        const flowBranch = this.items[id];
        const graphModel = this.graphModels[id];
        flowBranch.curvePoints = graphModel.inverse();
      },

      updateCurve(options: {
        id: number;
        curveType: CurveFormType;
        curvePoints: GraphPoint[];
      }) {
        const { id, curveType, curvePoints } = options;
        const flowBranch = this.items[id];

        // we seperate the update to the curve in two sets.

        // set 1: curve data (points, factor and type)
        const dataToChange = {
          id,
          curvePoints: cloneDeep(curvePoints),
          curveType: flowBranch.curveType
        };

        if (flowBranch.curveType !== curveType) {
          dataToChange.curveType = curveType;
        }

        this.update(dataToChange);
      },

      updatePointBounds(data: {
        id: number;
        maxFlowValue: number | null;
        minFlowValue: number | null;
        oldMinFlowValue: number | null;
        oldMaxFlowValue: number | null;
      }) {
        const {
          id,
          maxFlowValue,
          minFlowValue,
          oldMaxFlowValue,
          oldMinFlowValue
        } = data;
        const flowBranch = this.items[id];
        const points = flowBranch.curvePoints;

        if (points.length == 0) return;

        // TODO: Check min and max value transformation.

        if (oldMaxFlowValue === oldMinFlowValue) {
          if (minFlowValue != null) {
            points[0].x = minFlowValue;
          }
          if (maxFlowValue != null) {
            points[points.length - 1].x = maxFlowValue;
          }
        } else if (minFlowValue != null && maxFlowValue != null && oldMinFlowValue != null && oldMaxFlowValue != null){
          // adjust the x values of the points
          for (const point of points) {
            point.x =
              ((point.x - oldMinFlowValue) /
                (oldMaxFlowValue - oldMinFlowValue)) *
                (maxFlowValue - minFlowValue) +
              minFlowValue;
          }
        }

        // console.log(cloneDeep(flowBranch.curvePoints));
        this.updatePointYBounds({
          id,
          minFlowValue,
          maxFlowValue,
          oldMinFlowValue,
          oldMaxFlowValue
        });
        // console.log(cloneDeep(flowBranch.curvePoints));
      },

      /**
       * Scale the curve down/up according to maxInfluence.
       */
      updatePointYBounds(data: {
        id: number;
        maxFlowValue: number | null;
        minFlowValue: number | null;
        oldMinFlowValue: number | null;
        oldMaxFlowValue: number | null;
        multiplier: number;
      }) {
        const { id, minFlowValue, maxFlowValue, multiplier } = data;

        const flowBranch = this.items[id];

        const factor = flowBranch.branchType == "sink" ? 1 : -1;

        const newCurvePoints = getFlowBranchCurvePoints(
          flowBranch.curveType,
          minFlowValue ?? 0,
          maxFlowValue ?? 100,
          flowBranch.formula,
          factor,
          flowBranch.curvePoints,
          multiplier
        );
        flowBranch.curvePoints = newCurvePoints;
      },

      updateDirection(
        branchIds: number[],
        branchType: "source" | "sink",
        listIndex?: number
      ): void {
        let i = 0;
        for (const branchId of branchIds) {
          const branch = this.items[branchId];
          branch.branchType = branchType;

          if (listIndex != null) {
            // reorder the "order" array
            const orderArray = this.order;
            const index = orderArray.indexOf(branchId);
            orderArray.splice(index, 1);
            orderArray.splice(listIndex + i, 0, branchId);
          }
  
          this.invertCurve(branch);

          i++;
        }
      }
    },
    events: {
      async beforeCreate(branch) {
        if (branch.flowId == null) throw new Error("Flow ID is required");

        const flowStore = useFlowStore();
        const flow = flowStore.withExpressions[branch.flowId];
        if (flow.valueReferenceType == null || flow.valueReferenceId == null) {
          flow.valueInputMode = "expression";
        } else {
          const referencedElement = useModelStore().getItem(
            flow.valueReferenceType,
            flow.valueReferenceId
          );
          if (!referencedElement)
            throw new Error("Regulator element not found");
        }

        const minMax = flowStore.expressionValues[flow.uid];

        const factor = branch.branchType == "sink" ? 1 : -1;

        if (!branch.formula) {
          if (factor > 0) {
            branch.formula = "x";
          } else {
            branch.formula = "-x";
          }
        }

        if (branch.curvePoints == null || branch.curvePoints.length === 0) {
          // TODO: better curves if no min/max are available
          branch.curvePoints = [
            {
              id: 0,
              x: minMax.minValue ?? 0,
              y: (minMax.minValue ?? 100) * factor,
              bezierOut: { x: 0, y: 0 }
            },
            {
              id: 1,
              x: minMax.maxValue ?? 0,
              y: (minMax.maxValue ?? 100) * factor,
              bezierIn: { x: 0, y: 0 }
            }
          ];
        }
        if (branch.curveType == null) branch.curveType = "linear";

        return branch;
      },
      async created(branch) {
        const flow = useFlowStore().withExpressions[branch.flowId];
        if (!flow) return;

        useFlowStore().updateBranchesStrength(flow, branch.branchType);
      },
      async afterDelete(branch) {
        const flowStore = useFlowStore();
        const flow = flowStore.items[branch.flowId];
        if (!flow) return;

        const branches = this.byFlow[flow.id] || [];
        if (branches.length === 0) {
          await flowStore.delete(flow);
        }

        flowStore.updateBranchesStrength(flow, branch.branchType);
      }
    }
  }
);

function getFlowBranchCurvePoints(
  curveType: CurveFormType,
  minFlowValue: number,
  maxFlowValue: number,
  formula: string,
  factor = 1,
  oldCurvePoints: GraphPoint[] = [],
  multiplier = 1
) {
  const width = Math.abs(maxFlowValue - minFlowValue);
  const height = Math.abs(maxFlowValue - minFlowValue);
  const xCenter = minFlowValue + width / 2;

  switch (curveType) {
    case "linear":
      return [
        {
          id: 0,
          x: minFlowValue,
          y: minFlowValue * multiplier * factor,
          bezierOut: {
            x: 0,
            y: 0
          }
        },
        {
          id: 1,
          x: maxFlowValue,
          y: maxFlowValue * multiplier * factor,
          bezierIn: {
            x: 0,
            y: 0
          }
        }
      ];
    case "s":
      return [
        {
          id: 0,
          x: minFlowValue,
          y: minFlowValue * multiplier * factor,
          bezierOut: {
            x: Math.max(width / 2, 1),
            y: 0
          }
        },
        {
          id: 1,
          x: maxFlowValue,
          y: maxFlowValue * multiplier * factor,
          bezierIn: {
            x: Math.min(-width / 2, -1),
            y: 0
          }
        }
      ];

    case "saturation":
      return [
        {
          id: 0,
          x: minFlowValue,
          y: minFlowValue * multiplier * factor,
          bezierOut: {
            x: 0,
            y: 0
          }
        },
        {
          id: 1,
          x: maxFlowValue,
          y: maxFlowValue * multiplier * factor,
          bezierIn: {
            x: -width + 1,
            y: 0
          }
        }
      ];

    case "exponential":
      return [
        {
          id: 0,
          x: minFlowValue,
          y: minFlowValue * multiplier * factor,
          bezierOut: {
            x: width - 1,
            y: 0
          }
        },
        {
          id: 1,
          x: maxFlowValue,
          y: maxFlowValue * multiplier * factor,
          bezierIn: {
            x: 0,
            y: 0
          }
        }
      ];

    case "plateau":
      return [
        {
          id: 0,
          x: minFlowValue,
          y: minFlowValue * multiplier * factor,
          bezierOut: {
            x: 0,
            y: 0
          }
        },
        {
          id: 1,
          x: xCenter,
          y: (height / 2) * multiplier * factor,
          bezierIn: {
            x: -(width / 2),
            y: 0
          },
          bezierOut: {
            x: width / 2,
            y: 0
          }
        },
        {
          id: 2,
          x: maxFlowValue,
          y: maxFlowValue * multiplier * factor,
          bezierIn: {
            x: 0,
            y: 0
          }
        }
      ];

    case "gauss":
      return [
        {
          id: 0,
          x: minFlowValue,
          y: minFlowValue * multiplier * factor,
          bezierOut: {
            x: width / 5,
            y: 0
          }
        },
        {
          id: 1,
          x: xCenter,
          y: maxFlowValue * multiplier * factor,
          bezierIn: {
            x: -width / 5,
            y: 0
          },
          bezierOut: {
            x: width / 5,
            y: 0
          }
        },
        {
          id: 2,
          x: maxFlowValue,
          y: minFlowValue * multiplier * factor,
          bezierIn: {
            x: -width / 5,
            y: 0
          }
        }
      ];

    case "formula":
      return getFormulaPoints(
        `(${formula})*${multiplier}`,
        (maxFlowValue - minFlowValue) / 100,
        minFlowValue,
        maxFlowValue
      );

    case "custom":
      const points = oldCurvePoints;
      const maxInfluence = Math.max(
        Math.abs(maxFlowValue) * multiplier,
        Math.abs(minFlowValue) * multiplier
      );

      const posPoints = [];
      const negPoints = [];
      let max = 0;
      let min = 0;

      for (const point of points) {
        if (point.y >= maxInfluence) {
          point.y = maxInfluence;
          if (point.y > max) {
            posPoints.splice(0, posPoints.length);
          }
          max = maxInfluence;
          posPoints.push(point);
        } else if (point.y <= -maxInfluence) {
          if (point.y < min) {
            negPoints.splice(0, negPoints.length);
          }
          point.y = -maxInfluence;
          min = -maxInfluence;
          negPoints.push(point);
        } else if (point.y < maxInfluence && point.y > 0) {
          if (point.y > max) {
            max = point.y;
            posPoints.splice(0, posPoints.length);
          }
          posPoints.push(point);
        } else if (point.y > -maxInfluence && point.y < 0) {
          if (point.y < min) {
            min = point.y;
            negPoints.splice(0, negPoints.length);
          }
          negPoints.push(point);
        } else if (point.y == 0) {
          if (factor > 0) {
            posPoints.push(point);
          } else {
            negPoints.push(point);
          }
        }
      }
      for (const point of posPoints) {
        point.y = maxInfluence;
      }
      for (const point of negPoints) {
        point.y = -maxInfluence;
      }
      return points;
  }
}
